/**
 * qaSessionIO — session import/export helpers for the QA panel.
 *
 * File download / file parse / summary shaping live here as plain functions;
 * the clipboard + alert side-effects stay in QAPanelContent. Extracted from
 * QAPanel.tsx (mechanical split).
 */

import type { QASession } from './qaTypes';

export interface SessionStats {
  total: number;
  pass: number;
  fail: number;
  unchecked: number;
}

/** Trigger a browser download of the full session as pretty-printed JSON. */
export function downloadSessionJSON(session: QASession): void {
  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  const blob = new Blob([JSON.stringify(session, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `qa_report_${ts}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Read a previously-downloaded session file; resolves null for bad/foreign files. */
export function parseSessionFile(file: File): Promise<QASession | null> {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const loaded = JSON.parse(reader.result as string) as QASession;
        resolve(loaded.categories && loaded.templateId ? loaded : null);
      } catch {
        resolve(null); // ignore bad files
      }
    };
    reader.readAsText(file);
  });
}

/** Compact agent/chat-friendly summary: failures + noted items + progress. */
export function buildSummary(session: QASession, stats: SessionStats) {
  const issues = session.categories.flatMap(c =>
    c.items
      .filter(i => i.status === 'fail' || i.notes)
      .map(i => ({ category: c.label, item: i.label, status: i.status, note: i.notes || undefined }))
  );
  return {
    qa_summary: {
      template: session.templateName,
      exported: new Date().toISOString(),
      progress: stats,
      signed_off: session.overallSignOff,
      issues,
      notes: session.globalNotes || undefined,
    },
  };
}
